import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Search } from "lucide-react";
import { mutate } from "swr";
import { Checkbox } from "~/components/ui/checkbox";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { Agent, AgentList, AgentStatus, agentListPath } from "~/services/agents";
import { projectAgentsPath, setProjectAgents } from "~/services/projects";

interface ProjectAgentsDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attached: Agent[];
}

export const ProjectAgentsDialog = ({
  projectId,
  open,
  onOpenChange,
  attached,
}: ProjectAgentsDialogProps) => {
  const { data, error, isLoading } = useFetcher<AgentList>(open ? agentListPath() : null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  const attachedIds = useMemo(() => new Set(attached.map((a) => a.id)), [attached]);

  const filtered = useMemo(() => {
    const items = (data?.items ?? []).filter(
      (a) => !attachedIds.has(a.id) && a.status !== AgentStatus.DISABLED
    );
    const q = search.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter(
      (a) => a.display_name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)
    );
  }, [data?.items, attachedIds, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const { execute, isPending } = useAsyncMutation(
    (ids: string[]) => setProjectAgents(projectId, ids),
    {
      errorMessage: "Failed to add agents",
      onSuccess: async () => {
        await mutate(projectAgentsPath(projectId));
        onOpenChange(false);
      },
    }
  );

  const handleSave = () => {
    const newIds = [...attached.map((a) => a.id), ...Array.from(selected)];
    void execute(newIds);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Agents"
      description="Select agents to add to this project."
      contentClassName="sm:max-w-[640px]"
      onPrimary={handleSave}
      isPending={isPending}
      primaryLabel="Add"
      primaryPendingLabel="Adding…"
      primaryDisabled={selected.size === 0}
    >
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agents..."
          aria-label="Search agents"
          className={cn(
            "h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
            "text-sm placeholder:text-muted-foreground shadow-xs",
            "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          )}
        />
      </div>

      <div className="-mx-2 flex h-[50vh] flex-col overflow-y-auto">
        {isLoading && (
          <div role="status" className="flex flex-col gap-2 px-2 py-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-destructive"
          >
            <AlertCircle className="size-6" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load agents</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">
            {search ? "No agents match your search." : "No more agents to add."}
          </p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <ul className="flex flex-col" role="list">
            {filtered.map((agent) => (
              <AgentRow
                key={agent.id}
                agent={agent}
                checked={selected.has(agent.id)}
                onToggle={() => toggle(agent.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </FormDialog>
  );
};

interface AgentRowProps {
  agent: Agent;
  checked: boolean;
  onToggle: () => void;
}

const AgentRow = ({ agent, checked, onToggle }: AgentRowProps) => {
  const checkboxId = `agent-row-${agent.id}`;
  return (
    <li>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors",
          "hover:bg-accent/40"
        )}
      >
        <AgentAvatar agent={agent} size={36} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{agent.display_name}</p>
          <p className="truncate text-xs text-muted-foreground">{agent.description}</p>
        </div>
        <Checkbox id={checkboxId} checked={checked} onCheckedChange={onToggle} />
      </label>
    </li>
  );
};
